/**
 * MARKETING COMPASS — 30-day feedback prompt (spec §3.1.5).
 *
 * An in-app banner on the hub. In-app only for v1 — no email (Julie's call).
 *
 * Deliberately non-nagging:
 *   - ONE prompt per recommendation, enforced server-side on the recommendation
 *     doc rather than by client state, so clearing a browser doesn't restart it
 *   - snooze (a week) and dismiss (for good) both available up front
 *   - renders nothing at all until the server says a prompt is due
 *   - never blocks the hub: it sits above the cards and can be ignored, and if
 *     the check fails the banner simply doesn't appear
 *
 * Fields follow the Feedback Loop sheet: tried / outcome / optional spend /
 * optional free text, per product in the stack.
 */
import React, { useState, useEffect } from 'react';
import {
  getFeedbackPrompt, submitFeedback, snoozeFeedback, dismissFeedback,
  FEEDBACK_OUTCOMES, marketingErrorMessage,
} from './marketingApi';
import * as T from '../brand/tokens';
import { MessageSquareHeart, X, Clock, Loader2, Check } from 'lucide-react';

export default function FeedbackBanner() {
  const [prompt, setPrompt] = useState(null);       // null until due
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState({});       // productId -> {tried, outcome, spend, freeText}
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const p = await getFeedbackPrompt();
      if (!cancelled && p.due) setPrompt(p);
    })();
    return () => { cancelled = true; };
  }, []);

  if (!prompt || done) return null;

  const setEntry = (productId, patch) =>
    setEntries((prev) => ({ ...prev, [productId]: { ...(prev[productId] || {}), ...patch } }));

  // Only products the member has actually answered for are submitted — a
  // partial answer is more useful than a forced complete one.
  const answered = Object.entries(entries).filter(([, e]) => typeof e.tried === 'boolean');
  const canSubmit = answered.length > 0 && answered.every(([, e]) => !e.tried || e.outcome);

  const handleSubmit = async () => {
    setBusy(true); setError('');
    try {
      await submitFeedback(prompt.recommendationId, answered.map(([productId, e]) => ({
        productId,
        tried: e.tried,
        ...(e.tried ? { outcome: e.outcome } : {}),
        ...(e.spend != null && e.spend !== '' ? { spend: Number(e.spend) } : {}),
        ...(e.freeText ? { freeText: e.freeText } : {}),
      })));
      setDone(true);
    } catch (err) {
      setError(marketingErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleSnooze = async () => {
    setBusy(true);
    try { await snoozeFeedback(prompt.recommendationId); setDone(true); }
    catch { setError('Could not snooze just now.'); }
    finally { setBusy(false); }
  };

  const handleDismiss = async () => {
    setBusy(true);
    try { await dismissFeedback(prompt.recommendationId); setDone(true); }
    catch { setError('Could not dismiss just now.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="mb-4 rounded-2xl border border-gold-soft bg-parchment shadow-lg">
      <div className="p-4">
        <div className="flex items-start gap-3">
          <MessageSquareHeart className="mt-0.5 h-4 w-4 shrink-0 text-gold-deep" />
          <div className="min-w-0 flex-1">
            <p className={"font-cinzel font-bold " + T.BODY}>
              You picked your marketing stack a month ago — did you try any of it?
            </p>
            <p className={"mt-0.5 text-sm " + T.BODY_MUTED}>
              A minute of honesty here makes everyone's recommendations better. Skip anything
              you'd rather not answer.
            </p>

            {!open && (
              <div className="mt-3 flex flex-wrap gap-2">
                <button type="button" className={T.BTN_PRIMARY} onClick={() => setOpen(true)}>Tell us how it went</button>
                <button type="button" className={T.BTN_SECONDARY} onClick={handleSnooze} disabled={busy}>
                  <Clock className="h-3 w-3" /> Remind me later
                </button>
              </div>
            )}

            {open && (
              <div className="mt-4 space-y-3">
                {prompt.products.map((p) => {
                  const e = entries[p.productId] || {};
                  return (
                    <div key={p.productId} className={T.PANEL_MUTED + " px-3 py-3"}>
                      <p className={"font-semibold " + T.BODY}>{p.name}</p>

                      <div className="mt-2 flex flex-wrap gap-2">
                        {[{ v: true, l: 'I tried it' }, { v: false, l: "Didn't try it" }].map((opt) => (
                          <button
                            key={String(opt.v)}
                            type="button"
                            aria-pressed={e.tried === opt.v}
                            onClick={() => setEntry(p.productId, { tried: opt.v, outcome: undefined })}
                            className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${
                              e.tried === opt.v
                                ? 'border-gold bg-gold/15 font-semibold text-[#241f17]'
                                : 'border-[#d9cba6] text-[#4a4133] hover:bg-parchment-tint'
                            }`}
                          >
                            {opt.l}
                          </button>
                        ))}
                      </div>

                      {e.tried === true && (
                        <>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {FEEDBACK_OUTCOMES.map((o) => (
                              <button
                                key={o.value}
                                type="button"
                                aria-pressed={e.outcome === o.value}
                                onClick={() => setEntry(p.productId, { outcome: o.value })}
                                className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${
                                  e.outcome === o.value
                                    ? 'border-gold bg-gold/15 font-semibold text-[#241f17]'
                                    : 'border-[#d9cba6] text-[#4a4133] hover:bg-parchment-tint'
                                }`}
                              >
                                {o.label}
                              </button>
                            ))}
                          </div>

                          <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                            <label className={"flex-1 text-sm " + T.BODY_FAINT}>
                              Roughly what did you spend? <span className="opacity-70">(optional)</span>
                              <input
                                type="number"
                                min="0"
                                inputMode="decimal"
                                value={e.spend ?? ''}
                                onChange={(ev) => setEntry(p.productId, { spend: ev.target.value })}
                                placeholder="£"
                                className={T.INPUT}
                              />
                            </label>
                          </div>

                          <label className={"mt-2 block text-sm " + T.BODY_FAINT}>
                            Anything else? <span className="opacity-70">(optional)</span>
                            <textarea
                              rows={2}
                              maxLength={2000}
                              value={e.freeText || ''}
                              onChange={(ev) => setEntry(p.productId, { freeText: ev.target.value })}
                              className={T.INPUT}
                            />
                          </label>
                        </>
                      )}
                    </div>
                  );
                })}

                {error && <p className="text-sm text-[#8a3a1e]">{error}</p>}

                <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center">
                  <button type="button" className={T.BTN_PRIMARY} onClick={handleSubmit} disabled={!canSubmit || busy}>
                    {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                    Send feedback
                  </button>
                  <button type="button" className={T.BTN_SECONDARY} onClick={handleSnooze} disabled={busy}>
                    <Clock className="h-3 w-3" /> Remind me later
                  </button>
                </div>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={handleDismiss}
            disabled={busy}
            aria-label="Dismiss"
            className="shrink-0 rounded p-2 text-[#6d6151] hover:bg-gold/10 hover:text-[#241f17]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
